import React, { useLayoutEffect, useState } from "react";

import LightbulbIcon from "@uui/Lightbulb02";
import { find } from "lodash/fp";
import { useLocation, useSearchParams } from "react-router-dom";

import MultipleCalendarsWarning from "@app/analytics/components/MultipleCalendarsWarning";
import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import ConnectableIntegrationProvider from "@app/analytics/onboarding/components/ConnectableIntegrationProvider";
import NextButton from "@app/analytics/onboarding/components/NextButton";
import useOnboardingStore from "@app/analytics/onboarding/hooks/useOnboardingStore";
import CalendarStepSettingsRss from "@app/analytics/onboarding/steps/CalendarStep/CalendarStepSettingsRss";
import {
  StepCardContent,
  StepHeader,
  StepDescription,
  StepTitle,
  StepFooter,
} from "@app/analytics/onboarding/steps/styles";
import {
  CALENDAR_PROVIDERS,
  CALENDAR_PROVIDERS_BY_ID,
  INTEGRATION_PROVIDERS,
} from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import type { CalendarProviderId } from "@app/common/types/providerTypes";
import { usePageTitle } from "@app/utils/hooks";

import AutoAnimateContainer from "@rui/AutoAnimateContainer";
import InfoPanel from "@rui/InfoPanel";
import { Column } from "@rui/Stack";

import CalendarStepSettings from "./CalendarStepSettings";

// params that may be appended on a calendar connection, that we don't want
// to persist across subsequent connections
const QUERY_PARAMS_TO_STRIP_ON_CONNECT = [
  "action",
  "errorAction",
  "errorReturnTo",
  "success",
  "error",
];

const CalendarStep = () => {
  usePageTitle("Welcome - Calendar");

  const [pendingCalendar, setPendingCalendar] =
    useState<CalendarProviderId | null>(null);

  const location = useLocation();
  const [queryParams] = useSearchParams();
  const onboardingSource = useOnboardingStore((state) => state.context.source);

  const { goToNextStep } = useOnboardingStore();
  const { hasConnectedCalendarProviders } = useConnectedProviders();
  const { authorize, enable } = useIntegration();
  const { getEnableUrl } = useIntegrations();

  const handleAuthorizeCalendar = (providerId: CalendarProviderId) => {
    // remove any query params from a previous connection to prevent state pollution
    QUERY_PARAMS_TO_STRIP_ON_CONNECT.forEach((param) =>
      queryParams.delete(param)
    );

    authorize.mutate({
      providerId,
      returnRoute: location.pathname,
      connectParams: Object.fromEntries(queryParams.entries()),
    });
  };

  const handleConnectCalendar = (providerId: CalendarProviderId) => {
    setPendingCalendar(providerId);
    const enableUrl = getEnableUrl({ providerId });
    const providerConfig = CALENDAR_PROVIDERS_BY_ID[providerId];
    if (enableUrl) {
      return enable.mutate(enableUrl, {
        onSuccess: () => {
          setPendingCalendar(null);
          toast.success(`${providerConfig.displayName} connected!`);
        },
        // if the connection wasn't able to be automatically connected,
        // force them through the auth flow immediately
        onError: () => handleAuthorizeCalendar(providerId),
      });
    }
    return handleAuthorizeCalendar(providerId);
  };

  const connectAction = queryParams.get("action");
  const isConnectSuccess = queryParams.get("success") === "true";

  useLayoutEffect(() => {
    if (!connectAction) return;

    const integration = find({ connectAction }, INTEGRATION_PROVIDERS);

    if (!integration) return;

    if (isConnectSuccess) {
      toast.success(`${integration.displayName} connected!`);
    } else {
      toast.error(
        `There was an error connecting ${integration.displayName}. Please try again or contact support@read.ai if the problem persists.`
      );
    }
  }, [connectAction, isConnectSuccess]);

  return (
    <>
      <MultipleCalendarsWarning kind="added_onboarding" />
      <StepCardContent spacing={4}>
        <StepHeader>
          <StepTitle>Which calendar do you want to use with Read?</StepTitle>
          <StepDescription>
            {onboardingSource === "rss"
              ? "Read Smart Scheduler uses your calendar to make recommendations during event creation, and to provide Engagement Scores after an event occurs."
              : "We recommend selecting your busiest calendar, which is usually work-related."}
          </StepDescription>
          {onboardingSource !== "rss" && (
            <InfoPanel
              icon={<LightbulbIcon />}
              severity="info"
              sx={{
                color: "shades.neutral.600",
                maxWidth: "500px",
                margin: "12px auto 0 auto",
              }}
            >
              Did you know? Read automatically joins meetings on your calendar
              to provide AI-powered meeting summaries and recaps.
            </InfoPanel>
          )}
        </StepHeader>

        <Column spacing={{ xxs: 3, md: 5 }} width="100%" variant="center">
          <Column width="100%" maxWidth="300px" spacing={1}>
            {CALENDAR_PROVIDERS.map((provider) => (
              <ConnectableIntegrationProvider
                key={provider.id}
                // only show zoom if it's already connected
                hideIfNotConnected={provider.id === "zoom_calendar"}
                provider={provider}
                onConnect={(id) =>
                  handleConnectCalendar(id as CalendarProviderId)
                }
                isConnecting={pendingCalendar === provider.id}
              />
            ))}
          </Column>
          <AutoAnimateContainer
            show={hasConnectedCalendarProviders}
            width="100%"
          >
            {onboardingSource === "rss" ? (
              <CalendarStepSettingsRss />
            ) : (
              <CalendarStepSettings />
            )}
          </AutoAnimateContainer>
        </Column>
      </StepCardContent>
      <StepFooter>
        <NextButton
          onClick={() => goToNextStep("complete")}
          disabled={!hasConnectedCalendarProviders}
        />
      </StepFooter>
    </>
  );
};

export default CalendarStep;
